import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTeam } from '../../../shared/context/teamContext';
import teamService from '../../../shared/services/teamService';
import { motion } from 'framer-motion';

const industries = [
  { value: 'defi', label: 'DeFi' },
  { value: 'nft', label: 'NFT' },
  { value: 'gaming', label: 'Gaming' },
  { value: 'infrastructure', label: 'Infrastructure' },
  { value: 'exchange', label: 'Exchange' },
  { value: 'dao', label: 'DAO' },
  { value: 'wallet', label: 'Wallet' },
  { value: 'analytics', label: 'Analytics' },
  { value: 'other', label: 'Other' }
];

const teamSizes = [
  { value: '1-5', label: '1-5 members' },
  { value: '6-10', label: '6-10 members' },
  { value: '11-25', label: '11-25 members' },
  { value: '26-50', label: '26-50 members' },
  { value: '51-100', label: '51-100 members' },
  { value: '100+', label: 'More than 100 members' }
];

const getCurrentUserEmail = () => {
  const userString = localStorage.getItem('User');
  if (!userString) return '';
  try {
    const userObj = JSON.parse(userString);
    return userObj.email || '';
  } catch (err) {
    console.error('Error reading current user email:', err);
    return '';
  }
};

const steps = [
  { id: 'businessName', label: 'What is your business name?', type: 'text' },
  { id: 'industry', label: 'Which Web3 industry best describes you?', type: 'select', options: industries },
  { id: 'teamSize', label: 'How large is your team?', type: 'select', options: teamSizes },
  { id: 'websiteUrl', label: 'What is your primary website or app URL?', type: 'text' }
];

const TeamOnboarding = () => {
  const { selectedTeam, setSelectedTeam } = useTeam();
  const navigate = useNavigate();
  const { team: teamParam } = useParams();

  const defaultEmail = useMemo(() => getCurrentUserEmail(), []);

  const [formState, setFormState] = useState({
    businessName: selectedTeam?.onboarding?.businessName || selectedTeam?.name || '',
    industry: selectedTeam?.onboarding?.industry || 'other',
    otherIndustry: selectedTeam?.onboarding?.otherIndustry || '',
    teamSize: selectedTeam?.onboarding?.teamSize || '',
    description: selectedTeam?.description || 'Not provided yet',
    websiteUrl: selectedTeam?.onboarding?.websiteUrl || '',
    contactEmail: selectedTeam?.onboarding?.contactEmail || defaultEmail || ''
  });
  const [stepIndex, setStepIndex] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const currentStep = steps[stepIndex];
  const progressPercentage = ((stepIndex + 1) / steps.length) * 100;

  useEffect(() => {
    if (!selectedTeam) return;
    const slugBase = selectedTeam.name || selectedTeam._id;
    const encodedSlug = encodeURIComponent(teamParam || slugBase || '');

    if (!selectedTeam.isOwner) {
      navigate(`/${encodedSlug}/overview`, { replace: true });
      return;
    }

    if (selectedTeam.onboarding?.isComplete) {
      navigate(`/${encodedSlug}/overview`, { replace: true });
    }
  }, [selectedTeam, navigate, teamParam]);

  if (!selectedTeam || !selectedTeam._id) {
    return (
      <div className="min-h-screen w-full overflow-hidden bg-gradient-to-br from-[#f8fafc] via-white to-[#f1f5f9] relative flex items-center justify-center">
        <p className="text-[#0f172a]" style={{ fontFamily: 'Raleway, sans-serif' }}>
          Loading team information...
        </p>
      </div>
    );
  }

  const handleChange = (event) => {
    const { value } = event.target;
    setFormState(prev => ({ ...prev, [currentStep.id]: value }));
    setError('');
  };

  const validateStep = () => {
    const value = formState[currentStep.id];
    if (!value || (typeof value === 'string' && value.trim() === '')) {
      setError('This field is required.');
      return false;
    }

    // Special validation for industry - if "other" is selected, require otherIndustry text
    if (currentStep.id === 'industry' && value === 'other') {
      if (!formState.otherIndustry || formState.otherIndustry.trim() === '') {
        setError('Please specify your industry.');
        return false;
      }
    }

    if (currentStep.id === 'websiteUrl') {
      try {
        const url = new URL(value.trim());
        if (!url.protocol.startsWith('http')) {
          throw new Error();
        }
      } catch {
        setError('Enter a valid URL including http or https.');
        return false;
      }
    }

    setError('');
    return true;
  };

  const handleNext = async () => {
    if (!validateStep()) return;
    if (stepIndex < steps.length - 1) {
      setStepIndex(prev => prev + 1);
      return;
    }

    if (!selectedTeam?._id) {
      setError('Unable to locate your team. Please refresh and try again.');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        businessName: formState.businessName.trim(),
        industry: formState.industry === 'other' ? formState.otherIndustry.trim() : formState.industry,
        otherIndustry: formState.industry === 'other' ? formState.otherIndustry.trim() : '',
        teamSize: formState.teamSize,
        description: formState.description.trim() || 'Not provided yet',
        websiteUrl: formState.websiteUrl.trim(),
        contactEmail: formState.contactEmail.trim() || defaultEmail || '',
        goals: [],
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
      };

      const response = await teamService.completeOnboarding(selectedTeam._id, payload);
      if (response?.team) {
        const updatedTeam = response.team;
        setSelectedTeam(updatedTeam);
        
        // Update both selectedTeamData and selectedTeam name in localStorage
        const updatedTeamData = {
          ...updatedTeam,
          _lastFetched: Date.now()
        };
        localStorage.setItem('selectedTeamData', JSON.stringify(updatedTeamData));
        
        // Update the selectedTeam name if it changed
        const newTeamName = updatedTeam.name || updatedTeam.businessName || updatedTeam.onboarding?.businessName;
        if (newTeamName) {
          localStorage.setItem('selectedTeam', newTeamName);
        }
        
        window.dispatchEvent(new CustomEvent('teamDataUpdated', { detail: { teamData: updatedTeam } }));
        const teamSlug = encodeURIComponent(teamParam || newTeamName || updatedTeam.id || updatedTeam._id);
        navigate(`/${teamSlug}/overview`, { replace: true });
      }
    } catch (submitError) {
      console.error('Onboarding submission failed:', submitError);
      const message = submitError.response?.data?.message || 'Unable to save onboarding information.';
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleBack = () => {
    if (stepIndex > 0) {
      setError('');
      setStepIndex(prev => prev - 1);
    }
  };

  const isFormValid = () => {
    const value = formState[currentStep.id];
    if (!value || (typeof value === 'string' && value.trim() === '')) {
      return false;
    }
    // If industry is "other", also check that otherIndustry is filled
    if (currentStep.id === 'industry' && value === 'other') {
      return formState.otherIndustry && formState.otherIndustry.trim() !== '';
    }
    return true;
  };

  return (
    <div className="min-h-screen w-full overflow-hidden bg-[#f8fafc] relative" style={{ fontFamily: 'Raleway, sans-serif' }}>
      {/* Animated Background with Modern Design */}
      <div className="absolute inset-0 z-0">
        {/* Radial gradient: edges 8979FF → FFC9A4 → white (darker at sides) */}
        <div
          className="absolute inset-0"
          style={{
            background: 'radial-gradient(ellipse 85% 85% at 50% 50%, #ffffff 0%, rgba(255,255,255,0.97) 28%, rgba(255,201,164,0.2) 52%, rgba(137,121,255,0.25) 78%, rgba(137,121,255,0.3) 100%)',
          }}
        />
        
        {/* Animated floating orbs */}
        <motion.div
          className="absolute top-20 left-10 w-72 h-72 bg-[#8979FF]/10 rounded-full blur-3xl"
          animate={{
            x: [0, 50, 0],
            y: [0, 30, 0],
            scale: [1, 1.1, 1],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
        <motion.div
          className="absolute bottom-20 right-10 w-96 h-96 bg-[#FFC9A4]/10 rounded-full blur-3xl"
          animate={{
            x: [0, -40, 0],
            y: [0, -50, 0],
            scale: [1, 1.2, 1],
          }}
          transition={{
            duration: 25,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
        <motion.div
          className="absolute top-1/2 left-1/2 w-80 h-80 bg-[#8979FF]/5 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2"
          animate={{
            scale: [1, 1.15, 1],
            opacity: [0.3, 0.5, 0.3],
          }}
          transition={{
            duration: 15,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
        
        {/* Subtle dot pattern overlay */}
        <div 
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage: 'radial-gradient(circle, #8979FF 1px, transparent 1px)',
            backgroundSize: '40px 40px',
            backgroundPosition: '0 0',
          }}
        />
      </div>

      {/* Logo */}
      <div className="absolute top-6 left-6 z-20">
        <img 
          src="/login-logo.png" 
          alt="Cryptique Logo" 
          className="h-10 w-auto"
        />
      </div>

      {/* Progress Bar */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-[#DDE2EE] z-30">
        <div 
          className="h-full bg-[#8979FF] transition-all duration-300 ease-out"
          style={{ width: `${progressPercentage}%` }}
        />
      </div>

      {/* Onboarding Form */}
      <div className="relative z-10 min-h-screen flex items-center justify-center p-4 pt-20">
        <motion.div 
          className="w-full max-w-xl"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          style={{ zIndex: 20 }}
        >
          <div className="text-center mb-8">
            <h1 
              className="text-4xl mb-2 text-[#0f172a]" 
              style={{ fontFamily: 'Raleway, sans-serif', fontWeight: '500' }}
            >
              Let's set up your team
            </h1>
          </div>

          <div className="space-y-6">
            <form onSubmit={(e) => { e.preventDefault(); handleNext(); }} className="space-y-4">
              <div className="relative">
                <label 
                  htmlFor={currentStep.id}
                  className="block mb-2 text-sm text-[#475569]" 
                  style={{ fontFamily: 'Raleway, sans-serif' }}
                >
                  {currentStep.label}
                </label>
                
                {currentStep.id === 'industry' ? (
                  <div className="space-y-2">
                    {currentStep.options.map(option => (
                      <label
                        key={option.value}
                        className={`flex items-center py-2.5 px-3 border-2 rounded-lg cursor-pointer transition-all ${
                          formState.industry === option.value
                            ? 'border-[#8979FF] bg-[#f8fafc]'
                            : 'border-[#DDE2EE] bg-white hover:border-[#8979FF]/50'
                        }`}
                      >
                        <input
                          type="radio"
                          name="industry"
                          value={option.value}
                          checked={formState.industry === option.value}
                          onChange={(e) => {
                            setFormState(prev => ({ ...prev, industry: e.target.value }));
                            setError('');
                          }}
                          className="w-4 h-4 text-[#8979FF] focus:ring-2 focus:ring-[#8979FF]/20 focus:ring-offset-0 border-[#DDE2EE]"
                          style={{ accentColor: '#8979FF' }}
                        />
                        <span className="ml-3 text-[#0f172a] text-sm" style={{ fontFamily: 'Raleway, sans-serif' }}>
                          {option.label}
                        </span>
                      </label>
                    ))}
                    {formState.industry === 'other' && (
                      <div className="mt-3">
                        <input
                          type="text"
                          value={formState.otherIndustry || ''}
                          onChange={(e) => {
                            setFormState(prev => ({ ...prev, otherIndustry: e.target.value }));
                            setError('');
                          }}
                          className="w-full px-4 py-3 bg-white border border-[#DDE2EE] rounded-lg text-[#0f172a] placeholder-[#475569] focus:outline-none focus:ring-2 focus:ring-[#8979FF]/20 focus:border-[#8979FF]"
                          placeholder="Please specify your industry (Required)"
                          required
                          autoFocus
                          style={{ fontFamily: 'Raleway, sans-serif' }}
                        />
                      </div>
                    )}
                  </div>
                ) : currentStep.id === 'teamSize' ? (
                  <div className="space-y-2">
                    {currentStep.options.map(option => (
                      <label
                        key={option.value}
                        className={`flex items-center py-2.5 px-3 border-2 rounded-lg cursor-pointer transition-all ${
                          formState.teamSize === option.value
                            ? 'border-[#8979FF] bg-[#f8fafc]'
                            : 'border-[#DDE2EE] bg-white hover:border-[#8979FF]/50'
                        }`}
                      >
                        <input
                          type="radio"
                          name="teamSize"
                          value={option.value}
                          checked={formState.teamSize === option.value}
                          onChange={(e) => {
                            setFormState(prev => ({ ...prev, teamSize: e.target.value }));
                            setError('');
                          }}
                          className="w-4 h-4 text-[#8979FF] focus:ring-2 focus:ring-[#8979FF]/20 focus:ring-offset-0 border-[#DDE2EE]"
                          style={{ accentColor: '#8979FF' }}
                        />
                        <span className="ml-3 text-[#0f172a] text-sm" style={{ fontFamily: 'Raleway, sans-serif' }}>
                          {option.label}
                        </span>
                      </label>
                    ))}
                  </div>
                ) : (
                  <input
                    type="text"
                    id={currentStep.id}
                    value={formState[currentStep.id] || ''}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-white border border-[#DDE2EE] rounded-lg text-[#0f172a] placeholder-[#475569] focus:outline-none focus:ring-2 focus:ring-[#8979FF]/20 focus:border-[#8979FF]"
                    placeholder={currentStep.id === 'websiteUrl' ? 'https://example.com' : 'Enter your answer'}
                    required
                    autoFocus
                    style={{ fontFamily: 'Raleway, sans-serif' }}
                  />
                )}
              </div>

              {error && (
                <div className="p-3 rounded-lg text-sm bg-red-50 text-red-700 border border-red-200" style={{ fontFamily: 'Raleway, sans-serif' }}>
                  {error}
                </div>
              )}

              <div className="flex items-center justify-between gap-3">
                <button
                  type="button"
                  onClick={handleBack}
                  disabled={stepIndex === 0 || submitting}
                  className={`text-sm text-[#475569] hover:text-[#8979FF] transition-colors ${
                    stepIndex === 0 || submitting ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                  style={{ fontFamily: 'Raleway, sans-serif' }}
                >
                  ← Back
                </button>
              </div>

              <button
                type="submit"
                className={`w-full py-3 rounded-lg font-medium transition-colors ${
                  isFormValid() && !submitting
                    ? 'bg-[#8979FF] text-white hover:bg-[#7c6ae8]' 
                    : 'bg-[#DDE2EE] text-[#475569] cursor-not-allowed'
                }`}
                disabled={!isFormValid() || submitting}
                style={{ fontFamily: 'Raleway, sans-serif' }}
              >
                {submitting ? 'Saving...' : stepIndex === steps.length - 1 ? 'Finish' : 'Continue'}
              </button>
            </form>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default TeamOnboarding;
